/**
 * 管理员审批模式：媒体用 MediaApprovalGallery；草稿写入 product/company approval host。
 * 编辑模式 / 供应商：页面继续用 mediaUploader + MediaRejectBanner。
 * 页面需 inject: productApprovalHost 和/或 companyApprovalHost
 *
 * 媒体驳回关联（与入队一致）：
 *   Approval_Request_Content.UsageId = MediaFile.RID
 *   前端 media.id / fileID = MediaFile.RID
 *   → 用 usageId === media.id 匹配即可
 * 注意：MediaFile.UsageId 是父级（如认证行 RID），不要和 Approval.UsageId 混用。
 */
import {
  getMediaUploaderImageDataObj,
  getMediaUploaderVideoDataObj,
} from '@/_helpers/media-uploader-utils.js';
import {
  PRODUCT_APPROVAL_RESULT_CODE_SELECTED,
  PRODUCT_APPROVAL_RESULT_CODE_UNSELECTED,
  resolveProductRejectReasonText,
} from '@/pages/NewproductInfoCenter/productApprovalFieldMeta';
import { SHOW_EDIT_SUPPLIER_REJECT_REASON, shouldShowEditSupplierRejectPanels } from '@/constants/approvalRejectUi.js';

function parseUsageTypes(usageType) {
  if (Array.isArray(usageType)) {
    return usageType.map((x) => String(x || '').trim()).filter(Boolean);
  }
  return String(usageType || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function hostGetResults(host, types) {
  if (!host) return [];
  if (typeof host.getProductApprovalResultsByUsageType === 'function') {
    return host.getProductApprovalResultsByUsageType(types);
  }
  if (typeof host.getCompanyApprovalResultsByUsageType === 'function') {
    return host.getCompanyApprovalResultsByUsageType(types);
  }
  return [];
}

function hostGetDraft(host, row) {
  if (!host || !row) return row?.rejectReason || '';
  if (typeof host.getProductApprovalResultDraft === 'function') {
    return host.getProductApprovalResultDraft(row);
  }
  if (typeof host.getCompanyApprovalResultDraft === 'function') {
    return host.getCompanyApprovalResultDraft(row);
  }
  return row.rejectReason || '';
}

function hostSetDraft(host, row, value) {
  if (!host || !row) return;
  if (typeof host.setProductApprovalResultDraft === 'function') {
    host.setProductApprovalResultDraft(row, value);
    return;
  }
  if (typeof host.setCompanyApprovalResultDraft === 'function') {
    host.setCompanyApprovalResultDraft(row, value);
  }
}

function hostSetCode(host, row, code) {
  if (!host || !row) return;
  if (typeof host.setProductApprovalResultCode === 'function') {
    host.setProductApprovalResultCode(row, code);
    return;
  }
  if (typeof host.setCompanyApprovalResultCode === 'function') {
    host.setCompanyApprovalResultCode(row, code);
  }
}

/** 仅按 Approval.UsageId === MediaFile.RID（media.id）匹配 */
export function resolveMediaApprovalRowForMedia(rows, mediaId) {
  const list = rows || [];
  if (mediaId == null || String(mediaId).trim() === '') return null;
  const mediaKey = String(mediaId);
  return list.find((r) => String(r?.usageId ?? '') === mediaKey) || null;
}

function normalizeMediaRejectReasonText(rawReason) {
  const text = resolveProductRejectReasonText(rawReason);
  return String(text == null ? '' : text).trim();
}

/**
 * 编辑模式 / 供应商 mediaUploader / MediaRejectBanner 用驳回列表。
 * 按 usageId 匹配 + 非空驳回原因；不按 ResultCode 过滤。
 * （管理员审批模式走 MediaApprovalGallery，按 resultCode 标记，见 mediaApprovalGallery.vue）
 */
export function buildMediaRejectRecords(host, usageType, mediaArray, mediaType = 'image') {
  if (!host || !shouldShowEditSupplierRejectPanels(host)) return [];
  const rows = hostGetResults(host, parseUsageTypes(usageType));
  const normalized = (mediaArray || []).map((item) => (
    mediaType === 'video'
      ? getMediaUploaderVideoDataObj(item)
      : getMediaUploaderImageDataObj(item)
  ));
  return normalized
    .map((media) => {
      const row = resolveMediaApprovalRowForMedia(rows, media.id);
      if (!row) return null;
      const rejectReason = normalizeMediaRejectReasonText(
        hostGetDraft(host, row) || row.rejectReason || '',
      );
      if (!rejectReason) return null;
      return {
        mediaId: media.id,
        rejectReason,
        previewSrc: media.previewSrc || '',
        fileName: media.fileName || '',
        mediaType,
        actor: host.approvalRejectBy || 'AI',
      };
    })
    .filter(Boolean);
}

export default {
  computed: {
    mediaApprovalHost() {
      return this.productApprovalHost || this.companyApprovalHost || null;
    },
    /**
     * 管理员 + 审批模式 + 允许驳回面板（壳层）。
     * 有媒体时用 Gallery 预览；能否填驳回看 Approval_Request_Content。
     */
    isAdminApprovalMediaMode() {
      const host = this.mediaApprovalHost;
      if (!host || !host.fromAdmin || !host.isApprovalMode) return false;
      const panelsAllowed = host.showCompanyRejectPanels ?? host.showProductRejectPanels;
      return panelsAllowed !== false;
    },
    /** 审批只读：无图时仍显示原 uploader，但不可操作 */
    isApprovalMediaReadOnly() {
      return this.isAdminApprovalMediaMode;
    },
    showViewerMediaRejectUi() {
      if (!SHOW_EDIT_SUPPLIER_REJECT_REASON) return false;
      const host = this.mediaApprovalHost;
      if (!host) return true;
      return shouldShowEditSupplierRejectPanels(host);
    },
    mediaApprovalRejectionTemplates() {
      return this.mediaApprovalHost?.overallRejectTemplates || [];
    },
    mediaApprovalRejectionActor() {
      return this.mediaApprovalHost?.approvalRejectBy || '';
    },
  },
  methods: {
    /**
     * 审批模式且当前槽位已有媒体 → MediaApprovalGallery（预览）。
     * 无媒体 → 走原 mediaUploader。
     */
    shouldShowMediaApprovalGallery(mediaArray) {
      if (!this.isAdminApprovalMediaMode) return false;
      return Array.isArray(mediaArray) && mediaArray.length > 0;
    },
    /** 该媒体是否在 Approval_Request_Content 有行（映射字段入队后才有）→ 才允许填驳回 */
    canFillMediaRejectReason(usageType, mediaId) {
      if (!this.isAdminApprovalMediaMode || mediaId == null) return false;
      const rows = this.getMediaApprovalResults(usageType);
      return !!resolveMediaApprovalRowForMedia(rows, mediaId);
    },
    resolveViewerMediaRejectRecords(records) {
      return this.showViewerMediaRejectUi ? (records || []) : [];
    },
    getMediaApprovalResults(usageType) {
      return hostGetResults(this.mediaApprovalHost, parseUsageTypes(usageType));
    },
    /** 编辑模式：与审批 Gallery 同一套匹配规则 */
    getMediaRejectRecords(usageType, mediaArray, mediaType = 'image') {
      return buildMediaRejectRecords(
        this.mediaApprovalHost,
        usageType,
        mediaArray,
        mediaType,
      );
    },
    getMediaApprovalDraftMap(usageType, mediaArray) {
      const host = this.mediaApprovalHost;
      if (!host) return {};
      const rows = this.getMediaApprovalResults(usageType);
      const map = {};
      (mediaArray || []).forEach((item) => {
        const mediaId = item && item.id != null ? item.id : null;
        if (mediaId == null) return;
        const row = resolveMediaApprovalRowForMedia(rows, mediaId);
        if (!row) return;
        const draft = hostGetDraft(host, row);
        map[mediaId] = draft;
        map[String(mediaId)] = draft;
      });
      return map;
    },
    /** 只关联已有 Content 行，禁止管理员直提场景凭空造驳回记录 */
    ensureMediaApprovalResultRow(usageType, mediaId) {
      const host = this.mediaApprovalHost;
      if (!host || mediaId == null) return null;
      const types = parseUsageTypes(usageType);
      if (!types.length) return null;
      return resolveMediaApprovalRowForMedia(hostGetResults(host, types), mediaId);
    },
    onMediaApprovalDraftChange(usageType, payload) {
      const host = this.mediaApprovalHost;
      if (!host || !payload) return;
      const mediaId = payload.mediaId;
      const rejected = !!payload.rejected;
      const rejectReason = payload.rejectReason == null ? '' : String(payload.rejectReason);
      const row = this.ensureMediaApprovalResultRow(usageType, mediaId);
      if (!row) return;
      hostSetCode(
        host,
        row,
        rejected ? PRODUCT_APPROVAL_RESULT_CODE_SELECTED : PRODUCT_APPROVAL_RESULT_CODE_UNSELECTED
      );
      hostSetDraft(host, row, rejected ? rejectReason : '');
      if (rejected) {
        const panel = host.$refs && host.$refs.overallRejectPanel;
        if (panel && typeof panel.ensureActive === 'function') {
          panel.ensureActive();
        }
      }
      if (typeof host.refreshTabExclamationMarks === 'function') {
        host.refreshTabExclamationMarks();
      }
    },
  },
};
